import { useMutation, useQueryClient } from "react-query";
import { message } from "antd";
import { useDispatch } from "react-redux";
import {
  loginUser,
  registerUser,
  fetchUserWill,
  forgotPassword,
  validateResetToken,
  resetPassword,
  verifyEmail,
  requestEmailVerification,
} from "../../../services";
import { getIntroRouteForUser } from "../../../utils/use-stepper";
import storage from "../../../utils/storage";
import { setUserDetails, setUserLoggedIn } from "../store/actions";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import logoutUser from "../../../utils/logout-user";
import StoreTypes from "../../../store/types";

export const useLoginMutation = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { redirect } = useSelector((store) => store.auth);
  const { isLoading, mutate } = useMutation((login) => loginUser(login), {
    onSuccess: async ({ data }) => {
      storage.setToken(data.token);
      setUserLoggedIn(true);
      setUserDetails({ email: data?.userDetails?.email });

      let nextPath = "/";
      try {
        const payload = await queryClient.fetchQuery(
          ["userWill"],
          fetchUserWill,
        );
        const metadata = payload?.metadata;
        const isFullyComplete = metadata?.isFullyComplete;
        const userContext = metadata?.userContext;

        nextPath = getIntroRouteForUser(userContext, isFullyComplete);
      } catch (error) {
        console.error("Failed to fetch user will on login", error);
      }

      navigate(redirect || nextPath, { replace: true });
    },
    onError: async (err, loginVariables) => {
      const errorCode = err?.response?.data?.error;
      const email = loginVariables?.email; // Get email from login form

      // If email not verified, auto-send verification email and redirect
      if (errorCode === "EMAIL_NOT_VERIFIED") {
        try {
          // Pass email to backend so it can identify the user
          await requestEmailVerification(email);
          message.success("Verification email sent! Please check your inbox.");
          // Only navigate if email verification was sent successfully
          navigate("/email-verification-sent", {
            replace: true,
            state: { email },
          });
        } catch (verificationError) {
          const errorMsg =
            verificationError?.response?.data?.message ||
            "Failed to send verification email. Please try again.";
          message.error(errorMsg);
          // Do NOT navigate on error
        }
      } else {
        // Show other login errors
        const errorMessage =
          err?.response?.data?.message || "Login failed. Please try again.";
        message.error(errorMessage);
      }
    },
  });
  return { isLoading, mutate };
};

export const useRegisterMutation = () => {
  const navigate = useNavigate();
  const { isLoading, mutate } = useMutation((login) => registerUser(login), {
    onSuccess: async ({ data }) => {
      // Email NOT verified yet - no token stored
      // User will receive verification email and verify before getting authenticated
      message.success(
        data?.message ||
          "Account created successfully! Please verify your email.",
      );
      navigate("/register/complete", { replace: true });
    },
    onError: (err) => {
      const errorMessage =
        err?.response?.data?.message ||
        "Registration failed. Please try again.";
      message.error(errorMessage);
    },
  });

  return { isLoading, mutate };
};

export const useForgotPasswordMutation = () => {
  const { isLoading, isSuccess, isError, error, mutate } = useMutation(
    (data) => forgotPassword(data),
    {
      onSuccess: ({ data }) => {
        // Success handled in component
      },
      onError: (err) => {
        // Error handled in component
      },
    },
  );

  return { isLoading, isSuccess, isError, error, mutate };
};

export const useValidateResetTokenMutation = () => {
  const { isLoading, mutate, data } = useMutation(
    (data) => validateResetToken(data),
    {
      onSuccess: ({ data }) => {
        // Success handled in component
      },
      onError: (err) => {
        // Error handled in component
      },
    },
  );

  return { isLoading, mutate, data };
};

export const useResetPasswordMutation = () => {
  const navigate = useNavigate();
  const { isLoading, isSuccess, isError, error, mutate } = useMutation(
    (data) => resetPassword(data),
    {
      onSuccess: ({ data }) => {
        // Redirect to login after success
        setTimeout(() => {
          navigate("/login", {
            replace: true,
            state: { message: "Password reset successfully. Please login." },
          });
        }, 1500);
      },
      onError: (err) => {
        // Error handled in component
      },
    },
  );

  return { isLoading, isSuccess, isError, error, mutate };
};

export const useVerifyEmailMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { isLoading, isError, error, isSuccess, mutate } = useMutation(
    (data) => verifyEmail(data),
    {
      onSuccess: async ({ data }) => {
        try {
          // Email verified successfully - store token and mark user as logged in
          storage.setToken(data.token);
          setUserLoggedIn(true);
          setUserDetails({ email: data?.userDetails?.email });
          message.success("Email verified successfully!");

          // Refresh user will data in cache and update Redux metadata
          try {
            const payload = await queryClient.fetchQuery(
              ["userWill"],
              fetchUserWill,
            );
            // Dispatch the metadata to Redux so PageContainer knows email is verified
            dispatch({ 
              type: StoreTypes.SET_DETAILS, 
              payload: payload 
            });
            console.log("Metadata updated in Redux:", payload?.metadata);
          } catch (error) {
            console.error(
              "Failed to fetch user will after email verification",
              error,
            );
            // Continue anyway - not critical if this fails
          }
        } catch (err) {
          console.error("Error in email verification success handler:", err);
        }
      },
      onError: (err) => {
        console.error("Email verification failed:", err);
        const errorMessage =
          err?.response?.data?.message ||
          "Email verification failed. Please try again.";
        message.error(errorMessage);
      },
    },
  );

  return { isLoading, isError, error, isSuccess, mutate };
};

export const useLogoutUser = () => {
  const logout = () => {
    // Force a full app refresh before rendering the login route to ensure a clean state
    logoutUser();
  };

  return logout;
};
